import { useState } from "react";
import { deskCard, deskEyebrow, deskMuted, deskPrimaryBtn } from "./salesDeskStyles";
import type { SalesDeskLocationOption, SalesDeskStockDraft } from "./salesDeskStockRequest";

const fieldClass =
  "mt-2 block w-full rounded-lg border border-[#dce3dc] bg-white px-4 py-3 font-normal text-[#142d2d] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";

export function SalesDeskStockForm({
  draft,
  locations,
  onDraftChange,
  onSubmit,
  onBack,
}: {
  draft: SalesDeskStockDraft;
  locations: SalesDeskLocationOption[];
  onDraftChange: (draft: SalesDeskStockDraft) => void;
  onSubmit: (attachEvidence: boolean) => string | null;
  onBack: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [attachEvidence, setAttachEvidence] = useState(true);
  const prefilledFrom = draft.sourceSerial ?? "this sale";

  return (
    <div>
      <button type="button" onClick={onBack} className="text-sm font-semibold text-[#164b43] dark:text-emerald-300">
        ← Sales record
      </button>
      <p className={`${deskEyebrow} mt-4`}>Manager · Independent stock request</p>
      <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px]">Keep the lot ready for the next customer.</h1>
      <p className={`mt-3 max-w-[40rem] ${deskMuted}`}>This request is separate from the pending customer sale.</p>
      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className={deskCard}>
          <div className="flex min-h-[180px] items-center justify-center rounded-[13px] bg-[#e7eef2] text-sm text-[#687772] dark:bg-zinc-800 dark:text-zinc-400">
            No photo
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block text-sm font-semibold sm:col-span-2">
              Destination lot
              {locations.length > 0 ? (
                <select
                  aria-label="Destination lot"
                  value={draft.destinationLocationId ?? ""}
                  onChange={(event) => {
                    const location = locations.find((row) => row.id === event.target.value);
                    if (!location) return;
                    setError(null);
                    onDraftChange({ ...draft, destinationLocationId: location.id, destinationLabel: location.label });
                  }}
                  className={fieldClass}
                >
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  aria-label="Destination lot"
                  value={draft.destinationLabel}
                  onChange={(event) => {
                    setError(null);
                    onDraftChange({ ...draft, destinationLocationId: null, destinationLabel: event.target.value });
                  }}
                  className={fieldClass}
                />
              )}
            </label>
            <label className="block text-sm font-semibold">
              Size
              <input
                aria-label="Size"
                value={draft.size}
                onChange={(event) => onDraftChange({ ...draft, size: event.target.value })}
                className={fieldClass}
              />
            </label>
            <label className="block text-sm font-semibold">
              Color
              <input
                aria-label="Color"
                value={draft.color}
                onChange={(event) => onDraftChange({ ...draft, color: event.target.value })}
                className={fieldClass}
              />
            </label>
            <label className="block text-sm font-semibold sm:col-span-2">
              Electrical
              <input
                aria-label="Electrical"
                value={draft.electrical}
                onChange={(event) => onDraftChange({ ...draft, electrical: event.target.value })}
                className={fieldClass}
              />
            </label>
          </div>
          <p className={`mt-4 ${deskMuted}`}>Prefilled from {prefilledFrom}. Any edits affect only this stock request.</p>
          {error ? <p className="mt-4 text-sm text-red-800 dark:text-red-200">{error}</p> : null}
          <button
            type="button"
            className={`${deskPrimaryBtn} mt-4`}
            onClick={() => setError(onSubmit(attachEvidence))}
          >
            Submit stock request
          </button>
        </section>
        <section className={deskCard}>
          <h2 className="text-[21px] font-semibold">Evidence attached to the original match</h2>
          <p className={`mt-3 ${deskMuted}`}>
            {draft.evidenceIds.length === 0
              ? "No other sale shares this model."
              : `${draft.evidenceIds.length} other ${draft.evidenceIds.length === 1 ? "sale shares" : "sales share"} this model.`}
            {" "}
            Editing size, color, or electrical leaves this list on the original sale.
          </p>
          {draft.evidenceIds.length === 0 ? <p className={`mt-4 ${deskMuted}`}>Nothing to attach.</p> : null}
          <ul className="mt-3 grid gap-2">
            {draft.evidenceIds.map((id) => (
              <li key={id} className="break-all text-sm">
                {id.slice(0, 8)}
              </li>
            ))}
          </ul>
          {draft.evidenceIds.length > 0 ? (
            <label className="mt-4 flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={attachEvidence}
                onChange={(event) => setAttachEvidence(event.target.checked)}
              />
              Attach these record IDs
            </label>
          ) : null}
          <p className="mt-4 rounded-lg bg-[#f7f1df] px-4 py-3 text-sm text-[#5c5342] dark:bg-zinc-800 dark:text-zinc-200">
            If you edit the replacement, this evidence still describes the original configuration; it does not become an exact-match forecast.
          </p>
        </section>
      </div>
    </div>
  );
}
